import { createContext, MutableRefObject, useContext, useMemo, useRef, useState } from 'react';

import { DEFAULT_VENDOR_SELECTION_LIMIT } from '~/constants';
import { MarketplaceVendor } from '~/types/marketplace';

export const VendorContext = createContext({
  hasVendorsSelected: false,
  isAutoSelecting: false,
  requestQuoteModal: false,
  reviewQuoteRef: null as unknown as MutableRefObject<HTMLButtonElement | null>,
  selectedVendors: [] as MarketplaceVendor[],
  vendorSelectionLimitReached: false,
  setIsAutoSelecting: (_value: boolean | ((prevState: boolean) => boolean)) => {},
  setRequestQuoteModal: (_value: boolean | ((prevState: boolean) => boolean)) => {},
  setSelectedVendors: (_vendors: MarketplaceVendor[]) => {},
});

export const VendorProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedVendors, setSelectedVendors] = useState<MarketplaceVendor[]>([]);
  const [requestQuoteModal, setRequestQuoteModal] = useState<boolean>(false);
  const [isAutoSelecting, setIsAutoSelecting] = useState<boolean>(false);
  const reviewQuoteRef = useRef<HTMLButtonElement | null>(null);

  const hasVendorsSelected = useMemo(() => selectedVendors.length > 0, [selectedVendors]);
  const vendorSelectionLimitReached = useMemo(
    () => selectedVendors.length >= DEFAULT_VENDOR_SELECTION_LIMIT,
    [selectedVendors],
  );

  const contextValue = useMemo(
    () => ({
      hasVendorsSelected,
      isAutoSelecting,
      requestQuoteModal,
      reviewQuoteRef,
      selectedVendors,
      vendorSelectionLimitReached,
      setIsAutoSelecting,
      setRequestQuoteModal,
      setSelectedVendors,
    }),
    [hasVendorsSelected, isAutoSelecting, requestQuoteModal, selectedVendors, vendorSelectionLimitReached],
  );

  return <VendorContext.Provider value={contextValue}>{children}</VendorContext.Provider>;
};

export const useVendorContext = () => {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendorContext must be used within a VendorProvider');
  }
  return context;
};
